import { useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import {
  LayoutDashboard, Building2, FolderKanban, Users,
  Presentation, FileText, Receipt, Search, Clock, Settings,
  Maximize2, Minimize2,
} from 'lucide-react';
import { Toaster } from '../components/ui/sonner';
import { getBRAND_NAME, getBRAND_TAGLINE, getLOGO_URL, LOGO_FALLBACK_URL } from '../lib/brandConfig';
import { isAdmin, isEstimatorOnly, canManageUsers } from '../lib/auth';
import DashboardTab from '../components/dashboard/DashboardTab';
import QuotesTab from '../components/quote/QuotesTab';
import InvoicesTab from '../components/quote/InvoicesTab';
import ProposalsTab from '../components/proposal/ProposalsTab';
import ClientsTab from '../components/clients/ClientsTab';
import ContactsTab from '../components/contacts/ContactsTab';
import ProjectsTab from '../components/projects/ProjectsTab';
import SearchTab from '../components/search/SearchTab';
import AuditLogTab from '../components/audit/AuditLogTab';
import SettingsTab from '../components/settings/SettingsTab';

// ── Tab button ────────────────────────────────────────────────────────────────
function Tab({ active, onClick, icon: Icon, label, count }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold transition-all relative"
      style={{
        color: active ? 'var(--theme-tab-active-color)' : 'var(--theme-text-light)',
        borderBottom: active ? '2px solid var(--theme-accent)' : '2px solid transparent',
        background: 'none',
        cursor: 'pointer',
      }}
    >
      <Icon className="w-4 h-4" />
      {label}
      {count != null && (
        <span
          className="text-xs px-1.5 py-0.5 rounded-full"
          style={{
            background: active ? 'var(--theme-accent)' : 'var(--theme-border)',
            color: active ? 'var(--theme-tab-active-color)' : 'var(--theme-text-muted)',
            fontWeight: 600,
          }}
        >
          {count}
        </span>
      )}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
export default function AppShell() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'dashboard';

  const [isWide, setIsWide] = useState(() => localStorage.getItem('appWideScreen') === '1');
  const toggleWide = () => setIsWide(prev => {
    const next = !prev;
    localStorage.setItem('appWideScreen', next ? '1' : '0');
    return next;
  });

  // Dynamic brand state — updates on company-brand-updated event
  const [brandName, setBrandName] = useState(getBRAND_NAME);
  const [brandTagline, setBrandTagline] = useState(getBRAND_TAGLINE);
  const [brandLogo, setBrandLogo] = useState(getLOGO_URL);

  const refreshBrand = useCallback(() => {
    setBrandName(getBRAND_NAME());
    setBrandTagline(getBRAND_TAGLINE());
    setBrandLogo(getLOGO_URL());
  }, []);

  useEffect(() => {
    const handler = () => refreshBrand();
    window.addEventListener('company-brand-updated', handler);
    return () => window.removeEventListener('company-brand-updated', handler);
  }, [refreshBrand]);

  // Tab groups
  const row1 = [
    { key: 'dashboard', label: 'Dashboard',   icon: LayoutDashboard },
    { key: 'search',    label: 'Search',       icon: Search },
    { key: 'clients',   label: 'Clients',      icon: Building2 },
    { key: 'projects',  label: 'Projects',     icon: FolderKanban },
    { key: 'contacts',  label: 'Contacts',     icon: Users },
  ];
  const row2 = [
    { key: 'proposals',  label: 'Proposals',   icon: Presentation },
    { key: 'quotes',     label: 'Quotations',  icon: FileText },
    { key: 'invoices',   label: 'Invoices',    icon: Receipt },
    { key: 'audit-logs', label: 'Activity Log', icon: Clock },
    { key: 'settings',   label: 'Settings',    icon: Settings },
  ];

  // Navigate to a different tab from Dashboard
  function handleNavigateTab(tabKey) {
    setSearchParams({ tab: tabKey });
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--theme-page-bg)' }}>
      {/* Covers every top-level tab — ProposalsTab already calls toast() but had
          no Toaster mounted, so its messages never appeared */}
      <Toaster position="top-right" />

      {/* Header */}
      <div style={{ background: 'var(--theme-header-bg)' }} className="py-6 sm:py-10">
        <div className={`${isWide ? 'max-w-[1840px]' : 'max-w-[1000px]'} mx-auto px-4 sm:px-8`}>
          <div className="flex items-center gap-3 sm:gap-5">
            <img
              src={brandLogo}
              alt={brandName}
              className="h-16 w-16 sm:h-[120px] sm:w-[120px] object-contain flex-shrink-0"
              onError={(e) => {
                if (e.target.src !== LOGO_FALLBACK_URL) {
                  e.target.src = LOGO_FALLBACK_URL;
                } else {
                  e.target.style.display = 'none';
                }
              }}
            />
            <div>
              <h1
                className="text-xl sm:text-3xl font-bold text-white tracking-tight"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                {brandName}
              </h1>
              <p className="text-white/50 text-xs sm:text-sm mt-0.5">{brandTagline}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className={`${isWide ? 'max-w-[1840px]' : 'max-w-[1000px]'} mx-auto px-3 sm:px-8 -mt-6`}>
        <div className="rounded-lg shadow-xl overflow-hidden" style={{ background: 'var(--theme-card-bg)' }}>

          {/* Row 1 — Dashboard + Entity tabs */}
          <div
            className="flex border-b"
            style={{ borderColor: 'var(--theme-accent-sub)' }}
          >
            {row1.map(t => (
              <Tab
                key={t.key}
                active={activeTab === t.key}
                onClick={() => setSearchParams({ tab: t.key })}
                icon={t.icon}
                label={t.label}
              />
            ))}
          </div>

          {/* Accent divider between rows */}
          <div style={{ height: '1px', background: 'var(--theme-accent)', opacity: 0.4 }} />

          {/* Row 2 — Document tabs */}
          <div
            className="flex items-center border-b"
            style={{ borderColor: 'var(--theme-accent-sub)' }}
          >
            {row2.map(t => (
              <Tab
                key={t.key}
                active={activeTab === t.key}
                onClick={() => setSearchParams({ tab: t.key })}
                icon={t.icon}
                label={t.label}
              />
            ))}
            <button
              onClick={toggleWide}
              className="ml-auto mr-3 p-1.5 rounded transition-colors"
              style={{ color: isWide ? 'var(--theme-accent)' : 'var(--theme-text-light)' }}
              title={isWide ? 'Collapse to standard width' : 'Expand to widescreen (1920×1080)'}
            >
              {isWide ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
          </div>

          {/* Tab content */}
          <div className="p-4 sm:p-8">
            {activeTab === 'dashboard' && (
              <DashboardTab onNavigateTab={handleNavigateTab} />
            )}
            {activeTab === 'search' && (
              <SearchTab />
            )}
            {activeTab === 'quotes' && (
              <QuotesTab
                onEditQuote={(id) => navigate(`/quote/${id}`)}
                onOpenProposal={(quoteId) => navigate(`/proposal/new?quote_id=${quoteId}`)}
              />
            )}
            {activeTab === 'invoices' && (
              <InvoicesTab
                onEditInvoice={(id) => navigate(`/invoice/${id}`)}
              />
            )}
            {activeTab === 'proposals' && (
              <ProposalsTab
                onEditProposal={(id) => navigate(`/proposal/${id}`)}
              />
            )}
            {activeTab === 'clients' && (
              <ClientsTab
                onEditClient={(id) => navigate(`/clients/${id}`)}
              />
            )}
            {activeTab === 'projects' && (
              <ProjectsTab
                onEditProject={(id) => navigate(`/projects/${id}`)}
              />
            )}
            {activeTab === 'contacts' && (
              <ContactsTab
                onEditContact={(id) => navigate(`/contacts/${id}`)}
              />
            )}
            {activeTab === 'audit-logs' && (
              <AuditLogTab />
            )}
            {activeTab === 'settings' && (
              <SettingsTab />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
